import { useCallback, useEffect, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useChatContext } from "stream-chat-react";
import { disclosedNamesKeys } from "Api/Queries/Social/useDisclosedNamesMapQuery";
import type { Event, EventTypes } from "stream-chat";
import type { ChatChannelFilters } from "Types/Chat/chatFilters";

/** Every event after which the number of pending invites may differ. */
const PENDING_COUNT_EVENTS: ReadonlyArray<EventTypes> = [
  "notification.invited",
  "notification.invite_accepted",
  "notification.invite_rejected",
  "member.updated",
];

export const usePendingChatRequestCount = () => {
  const [count, setCount] = useState(0);
  const { client } = useChatContext();
  const queryClient = useQueryClient();

  const fetchCount = useCallback(async () => {
    if (!client.userID) {
      return;
    }

    const filters: ChatChannelFilters = {
      type: "messaging",
      members: { $in: [client.userID] },
      invite: "pending",
    };
    const channels = await client.queryChannels(
      filters,
      {},
      { limit: 30, watch: false },
    );
    setCount(channels.length);
  }, [client]);

  useEffect(() => {
    fetchCount();
  }, [fetchCount]);

  useEffect(() => {
    const handleEvent = ({ type }: Event) => {
      if (type === "notification.invited") {
        // The sender disclosed their name by asking (App ADR-0011); refresh the disclosed-names
        // map so the request row shows it right away instead of the @handle Stream carries.
        queryClient.invalidateQueries({ queryKey: disclosedNamesKeys.all });
      }

      if (PENDING_COUNT_EVENTS.includes(type)) {
        fetchCount();
      }
    };

    client.on(handleEvent);
    return () => {
      client.off(handleEvent);
    };
  }, [client, fetchCount, queryClient]);

  return count;
};
